import { TrendingKeywords } from './../models/trending-keywords';
import { Country } from './../models/country';
import { Gender } from './../models/gender';
import { AppSharedService } from './../services/app.service';
import { AppLookupResponse } from './../models/app-lookup-response';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { Sector } from '../models/sector';
import { LookupDataService } from '../services/lookup-data.service';
import { EducationLevel } from '../models/education-level';
import { JobType } from '../models/job-type';
import { SalaryType } from '../models/salary-type';
import { Currency } from '../models/currency';

@Injectable({
  providedIn: 'root'
})
export class AppStore {

  _sectors: BehaviorSubject<Sector[]> = new BehaviorSubject([]);
  public readonly sectors: Observable<Sector[]> = this._sectors.asObservable();

  _educationLevels: BehaviorSubject<EducationLevel[]> = new BehaviorSubject([]);
  public readonly educationLevels: Observable<EducationLevel[]> = this._educationLevels.asObservable();

  private _jobTypes: BehaviorSubject<JobType[]> = new BehaviorSubject([]);
  public readonly jobTypes: Observable<JobType[]> = this._jobTypes.asObservable();

  private _salaryTypes: BehaviorSubject<SalaryType[]> = new BehaviorSubject([]);
  public readonly salaryTypes: Observable<SalaryType[]> = this._salaryTypes.asObservable();

  private _languages: BehaviorSubject<AppLookupResponse[]> = new BehaviorSubject([]);
  public readonly languages$: Observable<AppLookupResponse[]> = this._languages.asObservable();

  private _countries: BehaviorSubject<Country[]> = new BehaviorSubject([]);
  public readonly countries: Observable<Country[]> = this._countries.asObservable();

  private _genders: BehaviorSubject<Gender[]> = new BehaviorSubject([]);
  public readonly genders: Observable<Gender[]> = this._genders.asObservable();

  private _trendingKeywords: BehaviorSubject<TrendingKeywords[]> = new BehaviorSubject([]);
  public readonly trendingKeywords: Observable<TrendingKeywords[]> = this._trendingKeywords.asObservable();

  private isLookupDataLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public readonly isLookupDataLoaded$: Observable<boolean> = this.isLookupDataLoaded.asObservable();

  private _currencies: BehaviorSubject<Currency[]> = new BehaviorSubject([]);
  public readonly currencies: Observable<Currency[]> = this._currencies.asObservable();

  constructor(private dataService: LookupDataService, private _appLooupService: AppSharedService) {
    this.loadInitialData();
  }

  loadInitialData(): Promise<void> {
    const sectors$ = this.dataService.loadSectors();
    const educationLevels$ = this.dataService.loadEducationLevels();
    const jobTypes$ = this.dataService.loadJobTypes();
    const salaryTypes$ = this.dataService.loadSalaryTypes();
    const languages$ = this._appLooupService.getLanguages();
    const countries$ = this.dataService.getCountries();
    const genders$ = this.dataService.loadGenders();
    const trendingKeywords$ = this.dataService.loadTrendingKeywords();
    const currencies$ = this.dataService.loadCurrencies();

    return forkJoin([
      sectors$,
      educationLevels$,
      jobTypes$,
      salaryTypes$,
      languages$,
      countries$,
      genders$,
      currencies$,
      trendingKeywords$
    ]).toPromise().then(results => {
      this._sectors.next(results[0]);
      this._educationLevels.next(results[1]);
      this._jobTypes.next(results[2]);
      this._salaryTypes.next(results[3]);
      this._languages.next(results[4]);
      this._countries.next(results[5]);
      this._genders.next(results[6]);
      this._currencies.next(results[7]);
      this._trendingKeywords.next(results[8]);
      this.isLookupDataLoaded.next(true);
    });
  }

  getEducationLevel(educationLevelId: string): EducationLevel {
    return this._educationLevels.getValue().find(el => el.id == educationLevelId);
  }

  getSalaryType(value: string): SalaryType {
    return this._salaryTypes.getValue().find(s => s.id == value);
  }

  getSalaryTypeByName(name: string): SalaryType {
    return this._salaryTypes.getValue().find(s => s.value == name);
  }

  getSector(sectorId: string): Sector {
    return this._sectors.getValue().find(s => s.id === sectorId);
  }

  getCountryByName(country: string): Country {
    return this._countries.getValue().find(c => c.name.toLocaleLowerCase() == country.toLocaleLowerCase());
  }

  getCountryById(countryId: string): Country | undefined {
    return this._countries.getValue().find(c => c.id == countryId);
  }

  getCurrencies(): Currency[] {
    return this._currencies.getValue();
  }

  getSelectedCurrency(code: string): Currency {
    return this._currencies.getValue().find(c => c.code == code);
  }

  setIsLookupDataLoaded = (flag: boolean) => this.isLookupDataLoaded.next(flag);

}
